import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileCheck2,
  Link2,
  Loader2,
  Mail,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import VacancyIntelligencePanel from './VacancyIntelligencePanel';

const WRITTEN_FORM_OPTIONS = [
  ['UNGEPRUEFT', 'Noch nicht geprüft'],
  ['NICHT_ERFUELLT', 'Nicht erfüllt'],
  ['SIGNIERTER_ANHANG_VORHANDEN', 'Signierter Anhang vorhanden'],
  ['ORIGINAL_NACHWEIS_ERFORDERLICH', 'Originalnachweis erforderlich'],
  ['MANUELL_BESTAETIGT', 'Manuell bestätigt'],
];

const REVIEW_OPTIONS = [
  ['NACHWEIS_ERFORDERLICH', 'Nachweis erforderlich'],
  ['FORMELL_ZU_PRUEFEN', 'Weitere formale Prüfung'],
  ['BESTAETIGT', 'Kündigung manuell bestätigen'],
  ['ABGELEHNT', 'Kündigung nicht bestätigen'],
];

const TerminationCaseWorkbench = ({ caseId, authHeaders, onClose, onChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [reviewForm, setReviewForm] = useState({
    review_status: 'NACHWEIS_ERFORDERLICH',
    written_form_status: 'UNGEPRUEFT',
    notice_date: '',
    requested_end_date: '',
    confirmed_end_date: '',
    earliest_contractual_end_date: '',
    termination_initiator: 'tenant',
    signed_attachment_present: false,
    sender_matches_tenant: false,
    confirm_legal_effect: false,
    review_note: '',
  });
  const [draft, setDraft] = useState({ subject: '', body: '' });
  const lastInteractionRef = useRef(Date.now());
  const pilotWorkStepRef = useRef('case_review');

  const notePilotInteraction = useCallback((event) => {
    lastInteractionRef.current = Date.now();
    const step = event?.target?.closest?.('[data-pilot-step]')?.dataset?.pilotStep;
    if (step) pilotWorkStepRef.current = step;
  }, []);

  const api = useCallback(async (url, options = {}) => {
    const headers = await authHeaders();
    const response = await fetch(url, {
      ...options,
      headers: { ...headers, ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...(options.headers || {}) },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.detail || payload.error || `Anfrage fehlgeschlagen (${response.status})`);
    return payload;
  }, [authHeaders]);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await api(`/api/cases/${caseId}`);
      setDetail(payload);
      const review = payload.termination_review;
      if (review) {
        setReviewForm((current) => ({
          ...current,
          review_status: review.review_status === 'UNGEPRUEFT' ? 'NACHWEIS_ERFORDERLICH' : review.review_status,
          written_form_status: review.written_form_status || 'UNGEPRUEFT',
          notice_date: review.notice_date || '',
          requested_end_date: review.requested_end_date || '',
          confirmed_end_date: review.confirmed_end_date || '',
          earliest_contractual_end_date: review.earliest_contractual_end_date || '',
          termination_initiator: review.termination_initiator || 'tenant',
          signed_attachment_present: Boolean(review.signed_attachment_present),
          sender_matches_tenant: Boolean(review.sender_matches_tenant),
          confirm_legal_effect: Boolean(review.legal_effect_confirmed),
        }));
      }
      const emailAction = [...(payload.actions || [])].reverse().find((item) => item.action_type === 'SEND_EXTERNAL_EMAIL' && !['cancelled', 'failed'].includes(item.status));
      if (emailAction?.proposal) {
        setDraft({ subject: emailAction.proposal.subject || '', body: emailAction.proposal.body || '' });
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [api, caseId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  useEffect(() => {
    const pilotCase = detail?.pilot_case;
    if (!pilotCase || pilotCase.enrollment_status !== 'active') return undefined;

    let sessionId = null;
    let stopped = false;
    let sequence = 0;
    const uniqueKey = (kind) => `touch_${pilotCase.id}_${kind}_${++sequence}_${globalThis.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(16).slice(2)}`}`;
    const send = async (url, body) => {
      try {
        const headers = await authHeaders();
        const response = await fetch(url, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          keepalive: true,
        });
        return response.ok ? response.json().catch(() => ({})) : null;
      } catch {
        return null;
      }
    };
    const start = async () => {
      if (stopped || sessionId || document.visibilityState !== 'visible') return;
      lastInteractionRef.current = Date.now();
      const nextSessionKey = uniqueKey('session');
      const payload = await send(`/api/operations/pilot/cases/${caseId}/touch/start`, {
        session_key: nextSessionKey,
        event_key: uniqueKey('opened'),
        work_step: pilotWorkStepRef.current,
      });
      if (!stopped && payload?.session?.id) sessionId = payload.session.id;
    };
    const heartbeat = async () => {
      if (stopped) return;
      if (!sessionId) {
        if (document.visibilityState === 'visible' && Date.now() - lastInteractionRef.current <= 120000) await start();
        return;
      }
      const payload = await send(`/api/operations/pilot/touch/${sessionId}/heartbeat`, {
        event_key: uniqueKey('heartbeat'),
        work_step: pilotWorkStepRef.current,
        page_visible: document.visibilityState === 'visible',
        last_interaction_at: new Date(lastInteractionRef.current).toISOString(),
      });
      if (payload?.expired) sessionId = null;
    };
    const closeSession = async (reason) => {
      const currentSessionId = sessionId;
      sessionId = null;
      if (!currentSessionId) return;
      await send(`/api/operations/pilot/touch/${currentSessionId}/close`, {
        event_key: uniqueKey(reason),
        work_step: pilotWorkStepRef.current,
        reason,
        page_visible: document.visibilityState === 'visible',
        last_interaction_at: new Date(lastInteractionRef.current).toISOString(),
      });
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') void closeSession('tab_hidden');
      else void start();
    };
    const handlePageHide = () => { void closeSession('case_left'); };

    void start();
    const timer = window.setInterval(() => { void heartbeat(); }, 30000);
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handlePageHide);
      void closeSession('dialog_closed');
    };
  }, [authHeaders,caseId,detail?.pilot_case?.id,detail?.pilot_case?.enrollment_status]);

  useEffect(() => {
    if (search.trim().length < 2) {
      setCandidates([]);
      return undefined;
    }
    const timer = window.setTimeout(async () => {
      try {
        const payload = await api(`/api/operations/assignment-candidates?q=${encodeURIComponent(search.trim())}`);
        setCandidates(payload.candidates || []);
      } catch (requestError) {
        setError(requestError.message);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [api, search]);

  const mutate = async (key, url, body) => {
    setSaving(key);
    setError('');
    try {
      await api(url, { method: 'POST', body: JSON.stringify(body) });
      await loadDetail();
      await onChanged?.();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving('');
    }
  };

  const activeEmailAction = useMemo(() => [...(detail?.actions || [])].reverse().find(
    (item) => item.action_type === 'SEND_EXTERNAL_EMAIL' && !['cancelled', 'failed', 'completed'].includes(item.status),
  ), [detail]);
  const pendingApproval = useMemo(() => [...(detail?.approvals || [])].reverse().find(
    (item) => item.action_id === activeEmailAction?.id && item.state === 'pending',
  ), [activeEmailAction, detail]);
  const approvedApproval = useMemo(() => [...(detail?.approvals || [])].reverse().find(
    (item) => item.action_id === activeEmailAction?.id && item.state === 'approved',
  ), [activeEmailAction, detail]);

  const assignCandidate = (candidate) => mutate(
    `assign-${candidate.tenant_id}`,
    `/api/operations/cases/${caseId}/assign`,
    { tenant_id: candidate.tenant_id, expected_version: detail.case.version },
  );

  const submitReview = () => mutate('review', `/api/operations/cases/${caseId}/termination-review`, {
    ...reviewForm,
    expected_version: detail.case.version,
    findings: { reviewed_in: 'dashboard_case_workbench' },
  });

  const createDraft = () => mutate('draft', `/api/operations/cases/${caseId}/termination-reply-draft`, {
    expected_version: detail.case.version,
    subject: draft.subject || undefined,
    body: draft.body || undefined,
  });

  const prepareMarketing = () => mutate('marketing', `/api/operations/cases/${caseId}/prepare-marketing`, {
    expected_version: detail.case.version,
  });

  const requestApproval = () => mutate('approval-request', `/api/actions/${activeEmailAction.id}/approval`, {});

  const decideApproval = (decision) => mutate(`approval-${decision}`, `/api/approvals/${pendingApproval.id}/decision`, {
    decision,
    proposal_hash: pendingApproval.proposal_hash,
    reason: decision === 'approved' ? 'Im Clara360 Case Workbench geprüft.' : 'Im Clara360 Case Workbench abgelehnt.',
  });

  const executeApproved = async () => {
    const confirmed = window.confirm(`Jetzt wirklich an ${activeEmailAction?.proposal?.recipient || 'den Empfänger'} versenden? Der Versand wird revisionssicher protokolliert.`);
    if (!confirmed) return;
    await mutate('execute', `/api/operations/actions/${activeEmailAction.id}/execute`, {
      approval_id: approvedApproval.id,
      proposal_hash: activeEmailAction.proposal_hash,
    });
  };

  if (!caseId) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-slate-950/60 p-3 sm:p-8"
      onPointerDownCapture={notePilotInteraction}
      onKeyDownCapture={notePilotInteraction}
      onInputCapture={notePilotInteraction}
      onFocusCapture={notePilotInteraction}
    >
      <div className="my-auto w-full max-w-5xl rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b bg-white px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">{detail?.case?.case_type === 'VACANCY_MARKETING' ? 'Vacancy Intelligence' : 'Kündigungsfall bearbeiten'}</h2>
            <p className="text-xs text-slate-500">Fall {String(caseId).slice(0, 8)} · eine operative Wahrheit</p>
            {detail?.pilot_case && <p className="mt-1 text-xs font-medium text-violet-700">Real-Vacancy-Pilot · {Math.round(Number(detail.pilot_case.human_touch_seconds || 0) / 6) / 10} Min. aktive Bearbeitung</p>}
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-5">
          {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
          {loading || !detail ? (
            <div className="flex min-h-56 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
          ) : (
            <>
              <section className="rounded-xl border bg-slate-50 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3 className="font-semibold text-slate-950">{detail.case.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{detail.case.summary}</p>
                  </div>
                  <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-900">{detail.case.state}</span>
                </div>
                {detail.case.blocker_detail && (
                  <div className="mt-3 flex items-start rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
                    <AlertTriangle className="mr-2 mt-0.5 h-4 w-4 shrink-0" />
                    <span><strong>{detail.case.blocker_code}:</strong> {detail.case.blocker_detail}</span>
                  </div>
                )}
                {detail.pilot_case && (
                  <div data-pilot-step="quality_review" className="mt-3 grid gap-3 rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm sm:grid-cols-4">
                    <label className="text-xs text-violet-800">Komplexität
                      <select value={detail.pilot_case.complexity_class || 'normal'} disabled={detail.pilot_case.enrollment_status !== 'active' || Boolean(saving)} onChange={(event) => mutate('pilot-complexity', `/api/operations/pilot/cases/${caseId}/complexity`, { complexity_class: event.target.value })} className="mt-1 w-full rounded-md border border-violet-200 bg-white p-2 text-sm text-slate-900">
                        <option value="simple">Einfach</option><option value="normal">Normal</option><option value="complex">Komplex</option>
                      </select>
                    </label>
                    <div><div className="text-xs text-violet-700">Baseline</div><div className="mt-1 font-semibold text-violet-950">{Number(detail.pilot_case.baseline_minutes || 0).toLocaleString('de-DE')} Min.</div></div>
                    <div><div className="text-xs text-violet-700">Human Touch</div><div className="mt-1 font-semibold text-violet-950">{(Number(detail.pilot_case.human_touch_seconds || 0) / 60).toLocaleString('de-DE', { maximumFractionDigits: 1 })} Min.</div></div>
                    <div><div className="text-xs text-violet-700">Evidence beim Erstreview</div><div className="mt-1 font-semibold text-violet-950">{detail.pilot_case.metadata?.first_review_evidence?.rate === undefined ? 'wird beim Öffnen fixiert' : `${Number(detail.pilot_case.metadata.first_review_evidence.rate).toLocaleString('de-DE')} %`}</div></div>
                  </div>
                )}
              </section>

              {detail.case.case_type === 'VACANCY_MARKETING' ? (
                <VacancyIntelligencePanel detail={detail} mutate={mutate} saving={saving} />
              ) : (
              <>
              <section className="rounded-xl border p-4">
                <div className="mb-3 flex items-center gap-2"><Link2 className="h-5 w-5 text-blue-600" /><h3 className="font-semibold">1. Mieter und Einheit zuordnen</h3></div>
                {detail.assignment ? (
                  <div className="rounded-lg bg-green-50 p-3 text-sm text-green-900">
                    <div className="flex items-center font-semibold"><CheckCircle2 className="mr-2 h-4 w-4" />Zuordnung bestätigt</div>
                    <p className="mt-1">{detail.assignment.masterdata_snapshot?.tenant?.name || detail.case.tenant_id}</p>
                    <p>{detail.assignment.masterdata_snapshot?.property?.property_name || detail.case.property_id} · Einheit {detail.assignment.masterdata_snapshot?.unit?.unit_identifier || detail.case.unit_id}</p>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name, E-Mail oder Einheit suchen" className="w-full rounded-lg border py-2.5 pl-9 pr-3 text-sm" />
                    </div>
                    <div className="mt-3 space-y-2">
                      {candidates.map((candidate) => (
                        <button key={candidate.tenant_id} type="button" onClick={() => assignCandidate(candidate)} disabled={Boolean(saving)} className="w-full rounded-lg border p-3 text-left hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50">
                          <div className="font-medium text-slate-900">{candidate.tenant_name}</div>
                          <div className="mt-1 text-xs text-slate-600">{candidate.tenant_email} · {candidate.property_name} · Einheit {candidate.unit_label}</div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </section>

              {detail.assignment && ['TERMINATION_REVIEW_REQUIRED', 'FORMAL_DEFECT_CLARIFICATION'].includes(detail.case.state) && (
                <section className="rounded-xl border p-4">
                  <div className="mb-3 flex items-center gap-2"><FileCheck2 className="h-5 w-5 text-blue-600" /><h3 className="font-semibold">2. Kündigung verifizieren</h3></div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="text-sm">Prüfergebnis<select value={reviewForm.review_status} onChange={(event) => setReviewForm({ ...reviewForm, review_status: event.target.value })} className="mt-1 w-full rounded-lg border p-2.5">{REVIEW_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                    <label className="text-sm">Schriftform/Nachweis<select value={reviewForm.written_form_status} onChange={(event) => setReviewForm({ ...reviewForm, written_form_status: event.target.value })} className="mt-1 w-full rounded-lg border p-2.5">{WRITTEN_FORM_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                    <label className="text-sm">Kündigungsdatum<input type="date" value={reviewForm.notice_date} onChange={(event) => setReviewForm({ ...reviewForm, notice_date: event.target.value })} className="mt-1 w-full rounded-lg border p-2.5" /></label>
                    <label className="text-sm">Gewünschtes Ende<input type="date" value={reviewForm.requested_end_date} onChange={(event) => setReviewForm({ ...reviewForm, requested_end_date: event.target.value })} className="mt-1 w-full rounded-lg border p-2.5" /></label>
                    <label className="text-sm">Frühestes Vertragsende<input type="date" value={reviewForm.earliest_contractual_end_date} onChange={(event) => setReviewForm({ ...reviewForm, earliest_contractual_end_date: event.target.value })} className="mt-1 w-full rounded-lg border p-2.5" /></label>
                    <label className="text-sm">Bestätigtes Ende<input type="date" value={reviewForm.confirmed_end_date} onChange={(event) => setReviewForm({ ...reviewForm, confirmed_end_date: event.target.value })} className="mt-1 w-full rounded-lg border p-2.5" /></label>
                  </div>
                  <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={reviewForm.signed_attachment_present} onChange={(event) => setReviewForm({ ...reviewForm, signed_attachment_present: event.target.checked })} />Signierter Anhang vorhanden</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={reviewForm.sender_matches_tenant} onChange={(event) => setReviewForm({ ...reviewForm, sender_matches_tenant: event.target.checked })} />Absender stimmt mit Mieter überein</label>
                  </div>
                  <textarea value={reviewForm.review_note} onChange={(event) => setReviewForm({ ...reviewForm, review_note: event.target.value })} placeholder="Prüfnotiz oder benötigter Nachweis" className="mt-3 min-h-20 w-full rounded-lg border p-2.5 text-sm" />
                  {reviewForm.review_status === 'BESTAETIGT' && (
                    <label className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                      <input type="checkbox" checked={reviewForm.confirm_legal_effect} onChange={(event) => setReviewForm({ ...reviewForm, confirm_legal_effect: event.target.checked })} className="mt-0.5" />
                      <span>Ich bestätige nach eigener Prüfung Schriftform, Parteien, Zugang, Frist und das angegebene Beendigungsdatum. Clara leitet diese Wirksamkeit nicht autonom ab.</span>
                    </label>
                  )}
                  <button type="button" onClick={submitReview} disabled={Boolean(saving)} className="mt-3 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving === 'review' && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Prüfung speichern</button>
                </section>
              )}

              {['TERMINATION_VERIFIED', 'FORMAL_DEFECT_CLARIFICATION'].includes(detail.case.state) && (
                <section className="rounded-xl border p-4">
                  <div className="mb-3 flex items-center gap-2"><Mail className="h-5 w-5 text-blue-600" /><h3 className="font-semibold">3. Antwort vorbereiten und freigeben</h3></div>
                  <input value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value })} placeholder="Betreff – leer lassen für Clara-Vorschlag" className="w-full rounded-lg border p-2.5 text-sm" />
                  <textarea value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} placeholder="Text – leer lassen für Clara-Vorschlag" className="mt-2 min-h-40 w-full rounded-lg border p-2.5 text-sm" />
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={createDraft} disabled={Boolean(saving)} className="rounded-lg border border-blue-300 px-4 py-2 text-sm font-semibold text-blue-800 disabled:opacity-50">Entwurf erzeugen/aktualisieren</button>
                    {activeEmailAction?.status === 'ready' && <button type="button" onClick={requestApproval} disabled={Boolean(saving)} className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><ShieldCheck className="mr-2 h-4 w-4" />Freigabe anfordern</button>}
                    {pendingApproval && <button type="button" onClick={() => decideApproval('approved')} disabled={Boolean(saving)} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Versand freigeben</button>}
                    {pendingApproval && <button type="button" onClick={() => decideApproval('rejected')} disabled={Boolean(saving)} className="rounded-lg border border-red-300 px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-50">Ablehnen</button>}
                    {approvedApproval && activeEmailAction?.status === 'approved' && <button type="button" onClick={executeApproved} disabled={Boolean(saving)} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Jetzt verbindlich versenden</button>}
                  </div>
                  {activeEmailAction?.proposal?.recipient && <p className="mt-3 text-xs text-slate-600">Empfänger aus bestätigter Zuordnung: <strong>{activeEmailAction.proposal.recipient}</strong></p>}
                  {pendingApproval && <p className="mt-2 break-all rounded bg-slate-50 p-2 font-mono text-[11px] text-slate-600">Versiegelter Proposal-Hash: {pendingApproval.proposal_hash}</p>}
                </section>
              )}

              {detail.case.state === 'TERMINATION_VERIFIED' && !detail.marketing_package && (
                <section className="rounded-xl border border-violet-200 bg-violet-50 p-4">
                  <h3 className="font-semibold text-violet-950">4. Neuvermietung vorbereiten</h3>
                  <p className="mt-1 text-sm text-violet-800">Clara übernimmt bestätigtes Beendigungsdatum, Objekt-, Einheits- und Mietdaten in einen verknüpften Vacancy-Case. Inserat und Veröffentlichung bleiben gesperrt.</p>
                  <button type="button" onClick={prepareMarketing} disabled={Boolean(saving)} className="mt-3 rounded-lg bg-violet-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    {saving === 'marketing' ? 'Wird vorbereitet …' : 'Vermarktungsfall erzeugen'}
                  </button>
                </section>
              )}

              {detail.marketing_package && (
                <section className="rounded-xl border border-violet-200 bg-violet-50 p-4">
                  <h3 className="font-semibold text-violet-950">Vermarktung vorbereitet</h3>
                  <p className="mt-1 text-sm text-violet-800">Status: {detail.marketing_package.status}. Der Vacancy-Case ist mit diesem Kündigungsfall verknüpft.</p>
                  <div className="mt-3 grid gap-2 text-sm text-violet-950 sm:grid-cols-2">
                    <div className="rounded-lg bg-white/70 p-3"><span className="text-xs text-violet-700">Verfügbar ab</span><div className="font-semibold">{detail.marketing_package.package_data?.available_from || 'offen'}</div></div>
                    <div className="rounded-lg bg-white/70 p-3"><span className="text-xs text-violet-700">Aktuelle Kaltmiete</span><div className="font-semibold">{detail.marketing_package.package_data?.current_lease?.cold_rent ?? 'offen'}</div></div>
                  </div>
                  {(detail.marketing_package.package_data?.missing_data || []).length > 0 && (
                    <div className="mt-3 rounded-lg bg-white/70 p-3 text-xs text-violet-900">
                      <strong>Noch zu ergänzen:</strong> {detail.marketing_package.package_data.missing_data.join(', ')}
                    </div>
                  )}
                </section>
              )}
              </>
              )}

              <section className="rounded-xl border p-4">
                <h3 className="font-semibold">Timeline</h3>
                <div className="mt-3 space-y-2">
                  {[...(detail.timeline || [])].reverse().slice(0, 8).map((entry) => (
                    <div key={entry.id} className="border-l-2 border-slate-200 pl-3 text-sm">
                      <div className="font-medium text-slate-800">{entry.title}</div>
                      <div className="text-xs text-slate-500">{new Date(entry.created_at).toLocaleString('de-DE')} · {entry.actor_type}</div>
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TerminationCaseWorkbench;
